import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import view_icon from "../../assets/images/view_icon.svg";
import edit_icon from "../../assets/images/edit_icon.svg";
import delete_icon from "../../assets/images/delete_icon.svg";

import DeletePopup from "../popups/DeletePopup";
import ProfileDetail from "../layout/ProfileDetail";

import { deleteUser } from "../../store/slices/userSlice";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { formatCurrency } from "../../utils/dateUtils";

import user from "../../assets/images/User.png";

const ClientsListTable = ({
  className,
  data = [],
  loading = false,
  currentPage,
  pageSize,
  totalRecords,
  onPageChange,
  pageKey,
  isDetailPage = false,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL || "";

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const [showProfileModal, setShowProfileModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);

  const totalPages = Math.ceil(totalRecords / pageSize);

  const handleDelete = (item) => {
    setSelectedItem(item);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (selectedItem?.id) {
      dispatch(deleteUser({ id: selectedItem.id, roleName: "client", key: pageKey }));
      setShowDeleteModal(false);
      setSelectedItem(null);
    }
  };

  const openProfile = (item) => {
    setSelectedUserId(item.id);
    setShowProfileModal(true);
  };



  const formatPhoneNumber = (phone) => {
    if (!phone) return "N/A";

    if (phone.trim().startsWith("+")) {
      return phone;
    }

    const digits = phone.replace(/\D/g, "");
    return digits.length >= 10 ? digits.slice(-10) : digits;
  };

  return (
    <>
      <div className={`custom_table table-responsive ${className || ""}`}>
        {/* TABLE */}
        <div className="table-wrapper">
          <table className="table mb-0">
            <thead>
              <tr>
                <th>Client Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Companies</th>
                <th>Employees</th>
                <th>Pay Avg</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center">
                    <div className="d-flex justify-content-center align-items-center py-4">
                      <div className="spinner-border text-primary me-2" role="status">
                        <span className="visually-hidden">Loading</span>
                      </div>
                      Loading
                    </div>
                  </td>
                </tr>
              ) : Array.isArray(data) && data.length > 0 ? (
                data.map((item) => {
                  const status = item?.isActive ? "Active" : "Inactive";

                  return (
                    <tr key={item.id}>
                      <td>
                        <div
                          className="d-flex align-items-center cursor-pointer"
                          onClick={() =>
                            navigate(`/clientdetail/${item.id}`)
                          }
                        >
                          <div className="user_img me-2">
                            <img
                              src={
                                item?.profile !== null
                                  ? BASE_URL + item.profile
                                  : user
                              }
                              alt="client"
                            />
                          </div>
                          {item?.name || "N/A"}
                        </div>
                      </td>

                      <td className="truncated-text" data-full-text={item?.email}>
                        {item?.email && item.email.length > (isDetailPage ? 15 : 30)
                          ? item.email.substring(0, isDetailPage ? 15 : 30) + "..."
                          : item?.email || "N/A"}
                      </td>
                      <td>{formatPhoneNumber(item?.phone)}</td>
                      <td>{item?.companiesCount ?? 0}</td>
                      <td>{item?.employeesCount ?? 0}</td>
                      <td>{formatCurrency(item?.payAvg)}</td>

                      <td>
                        <span className={status.toLowerCase()}>
                          {status}
                        </span>
                      </td>

                      <td>
                        <div className="action_links">
                          <span onClick={() => openProfile(item)}>
                            <img src={view_icon} alt="view" />
                          </span>

                          <span
                            onClick={() =>
                              navigate(`/editclient/${item.id}`, {
                                state: { from: location.pathname },
                              })
                            }
                          >
                            <img src={edit_icon} alt="edit" />
                          </span>

                          <span onClick={() => handleDelete(item)}>
                            <img src={delete_icon} alt="delete" />
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={7} className="text-center">
                    No Client Found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION FOOTER */}
        {totalRecords > 0 && (
          <div className="table_footer">
            <div className="footer_left">
              Showing {(currentPage - 1) * pageSize + 1}–
              {Math.min(currentPage * pageSize, totalRecords)} of{" "}
              {totalRecords} entries
            </div>

            <div className="footer_right">
              <button
                className="page_btn"
                disabled={currentPage === 1}
                onClick={() => onPageChange(currentPage - 1)}
              >
                ‹ Previous
              </button>

              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i}
                  className={`page_btn ${currentPage === i + 1 ? "active" : ""
                    }`}
                  onClick={() => onPageChange(i + 1)}
                >
                  {String(i + 1).padStart(2, "0")}
                </button>
              ))}

              <button
                className="page_btn"
                disabled={currentPage === totalPages}
                onClick={() => onPageChange(currentPage + 1)}
              >
                Next ›
              </button>
            </div>
          </div>
        )}
      </div>

      {/* DELETE POPUP */}
      <DeletePopup
        show={showDeleteModal}
        type="Client"
        handleClose={() => setShowDeleteModal(false)}
        onDelete={confirmDelete}
        allowedNames={selectedItem ? [selectedItem.name] : []}
      />

      {/* PROFILE POPUP */}
      {selectedUserId && (
        <ProfileDetail
          show={showProfileModal}
          onClose={() => {
            setShowProfileModal(false);
            setSelectedUserId(null);
          }}
          clientId={selectedUserId}
        />
      )}
    </>
  );
};

export default ClientsListTable;